import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const LocationContext = createContext(null);
export const useLocationCtx = () => useContext(LocationContext);

const EMPTY_LOCATION = {
  city: "",
  area: "",
  lat: null,
  lng: null,
  pincode: null,
  source: "unknown",
};

let saved = null;
try {
  saved = JSON.parse(localStorage.getItem("mmep_loc") || "null");
} catch {
  saved = null;
}

export const LocationProvider = ({ children }) => {
  const [tree, setTree] = useState([]);
  const [loc, setLoc] = useState(saved?.city ? saved : EMPTY_LOCATION);
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    api.get("/locations/tree")
      .then((r) => setTree(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (loc?.city) {
      localStorage.setItem("mmep_loc", JSON.stringify(loc));
    }
  }, [loc]);

  const selectArea = useCallback((area, city) => {
    const c = tree.find((x) => x.name === city);
    const a = c?.areas?.find((x) => x.name === area);

    if (!a) {
      setLoc({
        city,
        area: area || null,
        lat: null,
        lng: null,
        pincode: null,
        source: "manual",
      });
      return;
    }

    setLoc({
      city,
      area,
      lat: a?.lat ?? null,
      lng: a?.lng ?? null,
      pincode: a?.pincode ?? null,
      source: "manual",
    });
  }, [tree]);

  const locateByIP = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);

      const response = await fetch("https://ipapi.co/json/", {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });

      clearTimeout(timer);

      if (!response.ok) {
        throw new Error("IP location request failed");
      }

      const data = await response.json();
      const detectedCity = String(data?.city || "").trim();

      if (!detectedCity) {
        throw new Error("IP location did not return a city");
      }

      const search = await api.get(
        `/locations/search?q=${encodeURIComponent(detectedCity)}`
      );

      const results = Array.isArray(search.data) ? search.data : [];

      const exactCity =
        results.find(
          (x) =>
            x?.type === "city" &&
            String(x?.name || "").toLowerCase() === detectedCity.toLowerCase()
        ) ||
        results.find((x) => x?.type === "city");

      const cityName = exactCity?.name || detectedCity;

      setLoc({
        city: cityName,
        area: null,
        lat: null,
        lng: null,
        pincode: null,
        source: "ip",
      });

      return {
        city: cityName,
        area: null,
        source: "ip",
      };
    } catch (e) {
      return null;
    }
  }, []);

  const locateMe = useCallback(() => {
    if (!navigator.geolocation) {
      return locateByIP();
    }

    setGpsLoading(true);

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude: lat, longitude: lng } = pos.coords;

            const { data } = await api.get(
              `/locations/nearest?lat=${lat}&lng=${lng}`
            );

            if (!data?.city?.name || !data?.area?.name) {
              throw new Error("GPS location could not be matched");
            }

            const result = {
              city: data.city.name,
              area: data.area.name,
              lat,
              lng,
              pincode: data.area.pincode ?? null,
              source: "gps",
            };

            setLoc(result);
            resolve(result);
          } catch (e) {
            const fallback = await locateByIP();
            if (fallback) {
              resolve(fallback);
            } else {
              reject(e);
            }
          } finally {
            setGpsLoading(false);
          }
        },
        async (err) => {
          const fallback = await locateByIP();

          setGpsLoading(false);

          if (fallback) {
            resolve(fallback);
          } else {
            reject(err);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 300000,
        }
      );
    });
  }, [locateByIP]);

  useEffect(() => {
    if (saved?.city) return;

    const alreadyAttempted =
      sessionStorage.getItem("mmep_location_attempted") === "1";

    if (alreadyAttempted) return;

    sessionStorage.setItem("mmep_location_attempted", "1");

    const timer = setTimeout(() => {
      locateMe().catch(() => {});
    }, 700);

    return () => clearTimeout(timer);
  }, [locateMe]);

  const areas =
    tree.find((c) => c.name === loc.city)?.areas || [];

  return (
    <LocationContext.Provider
      value={{
        loc,
        tree,
        areas,
        selectArea,
        locateMe,
        gpsLoading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
